/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, May 2020
 */

import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import {
  FlowBlockRoot,
  FlowBlockPageBreak as BlockModel,
} from '@staffino/model';
import {
  PageBreakBlock as Component,
  useScroller,
} from '@staffino/flow-controls';
import FlowContext from '../flow-context';
import FlowStateManager from '../flow-state-manager';
import usePropertyTranslation from '../use-property-translation-hook';
import prepareFormValues from '../prepare-form-values';
import useBridge from '../use-bridge-hook';
import { ConditionEvaluator } from '../condition-evaluator';
import useEnvironmentBridge from '../use-environment-bridge';
import afterSubmit from './after-submit';

type Props = {
  block: BlockModel;
};

function calculateLabel(
  definition: FlowBlockRoot,
  state: FlowStateManager,
  submitLabel: string,
  continueLabel: string,
) {
  return state.isLastPage() ? submitLabel : continueLabel;
}

export default function PageBreakBlock({ block }: Props) {
  const { definition, stateManager, handleSubmit } = useContext(FlowContext);
  const { t } = useTranslation();
  const form = useFormContext();
  const bridge = useBridge();
  const environmentBridge = useEnvironmentBridge();
  const scroller = useScroller();

  const evaluator = useMemo(
    () => new ConditionEvaluator(stateManager, bridge, definition, form),
    [stateManager, bridge, definition, form],
  );

  const handleClick = useCallback(async () => {
    evaluator.applyActions(block.actions ?? []);
    stateManager
      .reevaluateConditions(form.getValues({ nest: true }), evaluator)
      .then(() => form.handleSubmit(handleSubmit)())
      .then(() => afterSubmit(form, () => scroller));
  }, [block.actions, evaluator, form, handleSubmit, scroller, stateManager]);

  const handleBack = useCallback(async () => {
    const formValues = await stateManager.previousPage();
    form.reset(prepareFormValues(formValues));

    environmentBridge.pushHistoryState({
      index: stateManager.getCurrentPageIndex(),
    });
  }, [form, stateManager, environmentBridge]);

  const translatedLabel = usePropertyTranslation(
    block.translations,
    'label',
    block.label ||
      calculateLabel(definition, stateManager, t('Submit'), t('Continue')),
  );
  const withBackButton = !stateManager.isFirstPage();
  return (
    <Component
      block={{ ...block, label: translatedLabel, withBackButton }}
      onBack={handleBack}
      onClick={handleClick}
    />
  );
}
